import * as ProgressPrimitive from "@radix-ui/react-progress";
import * as React from "react";

import { CustomToolTip } from "@/components/ui/CustomToolTip";
import { cn } from "@/utils/core/generalUtils";

type ProgressSegment = {
  value: number;
  color?: string;
};

type TooltipSegment = {
  label: string;
  percentage: number;
  description: string;
  color: string;
};

type Props = React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
  segments?: ProgressSegment[];
  tooltipData?: TooltipSegment[];
};

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  Props
>(({ className, segments, tooltipData, ...props }, ref) => {
  const progressBar = (
    <ProgressPrimitive.Root
      ref={ref}
      className={cn(
        "relative h-2.5 w-full overflow-hidden rounded bg-slate-200",
        className,
      )}
      {...props}
    >
      {segments?.map((segment, index) => (
        <ProgressPrimitive.Indicator
          key={index}
          className={cn(
            "h-full transition-all absolute",
            // segment.color ? `bg-${segment.color}` : "bg-primary",
          )}
          style={{
            background: segment.color || "#2744BD",
            width: `${segment.value}%`,
            zIndex: segments.length - index,
            left: `${segments
              .slice(0, index)
              .reduce((acc, segment) => acc + segment.value, 0)}%`,
          }}
        />
      ))}
    </ProgressPrimitive.Root>
  );

  if (tooltipData && tooltipData.length > 0) {
    const tooltipContent = (
      <div className='space-y-3 p-1 w-full'>
        {tooltipData.map((segment, index) => (
          <div key={index} className='space-y-1'>
            <div className='flex items-center gap-2'>
              <div
                className='h-4 w-4 rounded-full flex-shrink-0'
                style={{ backgroundColor: segment.color }}
              />

              <div className='flex flex-col w-full'>
                <div className='flex justify-between w-full gap-2'>
                  <span className='text-sm font-medium'>{segment.label}:</span>
                  <span className='text-sm font-medium'>
                    {segment.percentage}%
                  </span>
                </div>

                <p className='text-xs text-muted-foreground italic'>
                  {segment.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );

    return (
      <CustomToolTip
        toolTipTrigger={progressBar}
        toolTipContent={tooltipContent}
        toolTipContentProps={{
          className: "bg-white rounded-lg shadow-lg p-3 max-w-xs w-full",
        }}
      />
    );
  }

  return progressBar;
});

Progress.displayName = "Progress";

export { Progress };
